/**
 * Turn a Cata conversation into a Cata Backoffice QA test case (markdown).
 *
 * Debug mode → "Test case" in the chat header builds this from the chat, and the
 * file is imported in the backoffice (QA → Test cases → Import .md). The format
 * is the backoffice's: front matter + ## Preconditions / ## Steps / ## Expected
 * results / ## Notes. There's no `id`: the backoffice gives it the next free one.
 *
 * - every user message becomes a step (typed text, a clarification answer, or an
 *   upload the agent can't repeat — flagged in the preconditions), followed by a
 *   wait for the reply;
 * - recorded clicks (click trail, when the org has it on) become "Click …" steps
 *   in between;
 * - each turn's reply becomes a draft `judgment` expectation (the requested parts
 *   it covered, else an excerpt of what it said), plus a strict "no error" check.
 *   They describe what happened at the time, so they're meant to be reviewed.
 */
import type { Message } from '../components/features/chat/types';

export interface TrailClick {
  label: string;
  path?: string | null;
  clicked_at: string;
}

export interface QaCaseInput {
  messages: Message[];
  clicks?: TrailClick[];
  /** e.g. the order subject; defaults to the first user message */
  title?: string | null;
  /** where it came from, for the notes (e.g. "order 1234") */
  source?: string | null;
  now?: Date;
}

// Clicks on the debug tools themselves (and dialog chrome) aren't test steps.
const IGNORED_CLICK = /^(test case|trail|download \.md|copy( markdown)?|copied!?|close|cancel|debug( mode)?|[x×✕])$/i;

const clean = (s: string | null | undefined): string => (s || '').replace(/\s+/g, ' ').trim();
const quote = (s: string): string => `"${clean(s).replace(/"/g, "'")}"`;
const excerpt = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const messageText = (m: Message): string => clean(m.text || m.text_message || '');
const fileCount = (m: Message): number => (m.uploadedFiles?.length || 0) + (m.uploadedImage ? 1 : 0);

type Turn = { user: string; replies: Message[] };

export function buildQaCaseMarkdown({ messages, clicks = [], title, source, now = new Date() }: QaCaseInput): string {
  type Ev = { at: number; msg?: Message; click?: TrailClick };
  const timeline: Ev[] = [
    ...messages.filter(m => !m.isStreaming).map(m => ({ at: new Date(m.timestamp).getTime(), msg: m })),
    ...clicks.map(c => ({ at: new Date(c.clicked_at).getTime(), click: c })),
  ].sort((a, b) => a.at - b.at);

  const preconditions = ['Signed in with the test account', 'A new, empty chat'];
  const steps = ['Start a new chat'];
  const turns: Turn[] = [];
  let uploads = 0;
  let lastClick = '';

  for (const ev of timeline) {
    if (ev.click) {
      const label = clean(ev.click.label);
      if (!label || IGNORED_CLICK.test(label) || label === lastClick) continue;
      steps.push(`Click ${quote(label)}`);
      lastClick = label;
      continue;
    }
    const m = ev.msg!;
    lastClick = '';
    if (m.sender === 'bot') {
      if (turns.length) turns[turns.length - 1].replies.push(m);
      continue;
    }
    const text = messageText(m);
    const files = fileCount(m);
    if (files) {
      uploads += files;
      steps.push(`Attach the ${files === 1 ? 'file' : `${files} files`} from the original chat${text ? ` and type ${quote(text)}` : ''}, then send`);
    } else if (m.hiddenFromChat) {
      steps.push(`Answer the assistant's question with ${quote(text)}`);
    } else if (text) {
      steps.push(`Type ${quote(text)} in the chat input and send it`);
    } else {
      continue;
    }
    steps.push('Wait until the assistant has finished replying');
    turns.push({ user: text || '(attachment)', replies: [] });
  }

  if (uploads) {
    preconditions.push(`The original chat uploaded ${uploads} file${uploads === 1 ? '' : 's'}: the test agent can't attach files, so replace or remove those steps`);
  }

  const expected: string[] = [];
  let anyError = false;
  for (const turn of turns) {
    const replies = turn.replies.filter(r => messageText(r) || r.requested_parts?.length);
    const failed = turn.replies.some(r => r.isError || r.hasError || r.error_code);
    anyError = anyError || failed;
    const asked = excerpt(turn.user, 80);
    const parts = Array.from(new Set(turn.replies.flatMap(r => r.requested_parts || []).map(clean).filter(Boolean)));
    const last = replies[replies.length - 1];
    if (failed) {
      expected.push(`(judgment) The reply to ${quote(asked)} is a proper answer, not an error${last ? ` (the original chat showed: ${quote(excerpt(messageText(last), 160))})` : ''}`);
    } else if (parts.length) {
      expected.push(`(judgment) The reply to ${quote(asked)} covers the requested parts: ${parts.join(', ')}`);
    } else if (last) {
      expected.push(`(judgment) The reply to ${quote(asked)} gives an answer like: ${quote(excerpt(messageText(last), 220))}`);
    }
  }
  if (turns.length && !anyError) expected.push('(strict) No error message or "something went wrong" banner is shown');

  const firstAsk = turns.find(t => t.user !== '(attachment)')?.user;
  const caseTitle = clean(title) || (firstAsk ? `Chat: ${excerpt(firstAsk, 60)}` : 'Chat from Cata');
  const when = now.toISOString().slice(0, 16).replace('T', ' ');

  return [
    '---',
    `title: ${JSON.stringify(caseTitle)}`,  // a JSON string is a valid YAML scalar
    'area: chat',
    'priority: P2',
    'tags: [from-cata]',
    'start_url: /',
    `max_steps: ${clamp(10 + steps.length * 4, 15, 150)}`,
    `timeout_s: ${clamp(120 + turns.length * 120, 180, 3600)}`,
    '---',
    '',
    '## Preconditions',
    ...preconditions.map(p => `- ${p}`),
    '',
    '## Steps',
    ...steps.map((s, i) => `${i + 1}. ${s}`),
    '',
    '## Expected results',
    ...expected.map((e, i) => `- [E${i + 1}] ${e}`),
    '',
    '## Notes',
    `Exported from Cata debug mode on ${when} UTC${source ? ` (${clean(source)})` : ''}. ` +
      'The expected results describe what the assistant replied at the time: review and tighten them before running.',
    '',
  ].join('\n');
}

/** File name for the download, e.g. qa-case-chat-front-brake-pads-20260927-2150.md */
export function qaCaseFileName(markdown: string, now: Date = new Date()): string {
  const title = /^title:\s*(.+)$/m.exec(markdown)?.[1]?.replace(/^"|"$/g, '') || 'chat';
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'chat';
  const stamp = now.toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-');
  return `qa-case-${slug}-${stamp}.md`;
}
